import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table"
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Switch } from "@/components/ui/switch"
import { ArrowLeft, Plus, Trash2, GripVertical, Save, ChevronDown, Clock, Users, FileText, CheckSquare, Mail, Send } from "lucide-react"
import { CustomFieldValue } from "@/types/customer-success"
import CustomFieldsEditor from "@/components/customer-success/playbooks/CustomFieldsEditor"
import { playbookService } from "@/services/playbooks"
import { useToast } from "@/components/ui/use-toast"
import { useAuthContext } from "@/contexts/AuthContext"

export default function NewPlaybookTemplate() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user } = useAuthContext()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [taskGroups, setTaskGroups] = useState([
    {
      id: crypto.randomUUID(),
      title: "Getting started",
      icon: "🎯",
      tasks: [
        { 
          id: crypto.randomUUID(), 
          title: "First task", 
          description: "", 
          type: "task",
          assignee_type: "fixed", // Mantendo apenas responsáveis
          assignees: [],
          deadline_type: "days", // Mantendo apenas prazo em dias
          deadline_value: 2,
          deadline_reference: "launch", // launch, previous_task
          detailed_description: "",
          custom_fields: [] as CustomFieldValue[]
        }
      ]
    }
  ])
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false)
  const [newGroupTitle, setNewGroupTitle] = useState("")
  const [newGroupIcon, setNewGroupIcon] = useState("🎯")
  
  const handleAddGroup = () => {
    if (newGroupTitle.trim()) {
      setTaskGroups([
        ...taskGroups,
        {
          id: crypto.randomUUID(),
          title: newGroupTitle,
          icon: newGroupIcon,
          tasks: []
        }
      ])
      setNewGroupTitle("")
      setNewGroupIcon("🎯")
      setShowAddGroupDialog(false)
    }
  }
  
  const handleAddTask = (groupId: string) => {
    const newTaskId = crypto.randomUUID();
    setTaskGroups(taskGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          tasks: [
            ...group.tasks,
            { 
              id: newTaskId, 
              title: "Nova tarefa", 
              description: "", 
              type: "task",
              assignee_type: "fixed", // Mantendo apenas responsáveis
              assignees: [],
              deadline_type: "days", // Mantendo apenas prazo em dias
              deadline_value: 2,
              deadline_reference: "launch", // launch, previous_task
              detailed_description: "",
              custom_fields: [] as CustomFieldValue[]
            }
          ]
        }
      }
      return group
    }))
  }
  
  const handleRemoveTask = (groupId: string, taskId: string) => {
    setTaskGroups(taskGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          tasks: group.tasks.filter(task => task.id !== taskId)
        }
      }
      return group
    }))
  }
  
  const handleRemoveGroup = (groupId: string) => {
    setTaskGroups(taskGroups.filter(group => group.id !== groupId))
  }
  
  const handleUpdateTask = (groupId: string, taskId: string, field: string, value: any) => {
    setTaskGroups(taskGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          tasks: group.tasks.map(task => {
            if (task.id === taskId) {
              return { ...task, [field]: value }
            }
            return task
          })
        }
      }
      return group
    }))
  }
  
  const handleUpdateGroup = (groupId: string, field: string, value: string) => {
    setTaskGroups(taskGroups.map(group => {
      if (group.id === groupId) {
        return { ...group, [field]: value }
      }
      return group
    }))
  }
  
  const handleUpdateCustomFields = (groupId: string, taskId: string, customFields: CustomFieldValue[]) => {
    handleUpdateTask(groupId, taskId, 'custom_fields', customFields)
  }
  
  const handleSaveTemplate = async () => {
    // Validar os campos obrigatórios
    if (!name.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, informe um nome para o template.",
        variant: "destructive"
      })
      return
    }
    
    if (!user) {
      toast({
        title: "Usuário não autenticado",
        description: "Você precisa estar logado para salvar um template.",
        variant: "destructive"
      })
      return
    }
    
    setIsLoading(true)
    
    try {
      // Preparar o conteúdo do template
      const templateContent = {
        groups: taskGroups.map(group => ({
          id: group.id,
          title: group.title,
          icon: group.icon,
          tasks: group.tasks.map(task => ({
            id: task.id,
            title: task.title,
            description: task.description,
            task_type: task.type,
            assignee_type: "fixed",
            assignees: task.assignees,
            deadline_type: task.deadline_type,
            deadline_value: task.deadline_value,
            deadline_reference: task.deadline_reference,
            detailed_description: task.detailed_description,
            custom_fields: task.custom_fields,
            // Adicionar informação sobre a tarefa referenciada se for uma tarefa específica
            referenced_task_id: task.deadline_reference.startsWith('task_') 
              ? task.deadline_reference.replace('task_', '') 
              : undefined
          }))
        }))
      }
      
      // Salvar o template no Supabase
      const newTemplate = await playbookService.templates.create({
        name,
        description,
        template_content: JSON.stringify(templateContent),
        created_by: user.id,
        updated_by: user.id
      } as any)
      
      // Agora salvar os grupos na tabela playbook_template_task_groups
      try {
        console.log("Criando grupos na tabela...");
        // Salvar cada grupo na tabela de grupos
        for (let i = 0; i < taskGroups.length; i++) {
          const group = taskGroups[i];
          
          // Criar o grupo
          const savedGroup = await playbookService.templateTaskGroups.create({
            playbook_template_id: newTemplate.id,
            title: group.title,
            icon: group.icon,
            group_order: i
          });
          
          // Criar todas as tarefas do grupo
          console.log(`Criando ${group.tasks.length} tarefas para o grupo ${group.title}...`);
          for (let j = 0; j < group.tasks.length; j++) {
            const task = group.tasks[j];
            await playbookService.templateTasks.create({
              playbook_template_group_id: savedGroup.id,
              title: task.title,
              description: task.description,
              task_type: task.type,
              assignee_type: "fixed",
              assignees: task.assignees,
              deadline_type: task.deadline_type,
              deadline_value: task.deadline_value,
              deadline_reference: task.deadline_reference,
              detailed_description: task.detailed_description,
              custom_fields: task.custom_fields,
              task_order: j
            });
          }
        }
        console.log("Grupos e tarefas salvos na tabela com sucesso");
      } catch (error) {
        console.error("Erro ao salvar grupos e tarefas na tabela:", error);
        // Não vamos interromper o fluxo por falha na tabela de grupos
      }
      
      toast({
        title: "Template criado com sucesso",
        description: "O template de playbook foi salvo e está pronto para uso."
      })
      
      // Navegar de volta para a página de playbooks
      navigate("/customer-success/playbooks")
    } catch (error) {
      console.error("Erro ao salvar o template:", error)
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível criar o template. Por favor, tente novamente.",
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  // Função para obter o nome da tarefa referenciada
  const getTaskReferenceDisplay = (referenceValue: string, currentTaskId: string) => {
    if (referenceValue === 'launch') return 'lançamento';
    if (referenceValue === 'previous_task') return 'tarefa anterior';
    
    if (referenceValue.startsWith('task_')) {
      const refTaskId = referenceValue.replace('task_', '');
      const refTask = taskGroups.flatMap(g => g.tasks).find(t => t.id === refTaskId);
      return refTask ? `"${refTask.title}"` : 'tarefa específica';
    }
    
    return 'tarefa específica';
  };
  
  // Função para lidar com o fim do drag and drop
  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // Se não houver destino ou se o item for solto na mesma posição, não faz nada
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }

    // Encontra o grupo de origem
    const sourceGroup = taskGroups.find(group => group.id === source.droppableId);
    if (!sourceGroup) return;

    // Cria uma cópia das tarefas do grupo
    const newTaskGroups = [...taskGroups];
    const sourceGroupIndex = newTaskGroups.findIndex(group => group.id === source.droppableId);
    
    // Remove a tarefa da posição original
    const [movedTask] = newTaskGroups[sourceGroupIndex].tasks.splice(source.index, 1);
    
    // Insere a tarefa na nova posição
    newTaskGroups[sourceGroupIndex].tasks.splice(destination.index, 0, movedTask);
    
    // Atualiza o estado
    setTaskGroups(newTaskGroups);
  };
  
  return (
    <div className="w-full px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold">Novo Template de Playbook</h1>
          <p className="text-muted-foreground">
            Crie um template para padronizar suas atividades de customer success
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild className="gap-1.5">
            <Link to="/customer-success/playbooks">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Voltar para playbooks
            </Link>
          </Button>
          <Button onClick={handleSaveTemplate} disabled={isLoading}>
            {isLoading ? (
              <>Salvando...</>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Salvar Template
              </>
            )}
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Detalhes do Template</CardTitle>
            <CardDescription>Informações básicas sobre o template</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nome do Template</Label>
                <Input 
                  id="name" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  placeholder="ex: Onboarding de Cliente" 
                />
              </div>
              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea 
                  id="description" 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  placeholder="Descreva o propósito deste template de playbook" 
                  rows={3}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Grupos de Tarefas</CardTitle>
              <CardDescription>Organize as tarefas do playbook em grupos relacionados</CardDescription>
            </div>
            <Dialog open={showAddGroupDialog} onOpenChange={setShowAddGroupDialog}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar Grupo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Adicionar Grupo de Tarefas</DialogTitle>
                  <DialogDescription>
                    Crie um novo grupo de tarefas relacionadas para seu playbook.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="groupTitle">Título do Grupo</Label>
                    <Input 
                      id="groupTitle" 
                      value={newGroupTitle} 
                      onChange={(e) => setNewGroupTitle(e.target.value)} 
                      placeholder="ex: Primeiros Passos" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="groupIcon">Ícone</Label>
                    <Select value={newGroupIcon} onValueChange={setNewGroupIcon}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um ícone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="🎯">🎯 Alvo</SelectItem>
                        <SelectItem value="🔧">🔧 Configuração</SelectItem>
                        <SelectItem value="📚">📚 Treinamento</SelectItem>
                        <SelectItem value="🚀">🚀 Lançamento</SelectItem>
                        <SelectItem value="📋">📋 Checklist</SelectItem>
                        <SelectItem value="🤝">🤝 Reunião</SelectItem>
                        <SelectItem value="✅">✅ Conclusão</SelectItem>
                        <SelectItem value="⏱️">⏱️ Cronograma</SelectItem>
                        <SelectItem value="💬">💬 Discussão</SelectItem>
                        <SelectItem value="📝">📝 Documento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddGroupDialog(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleAddGroup}>Adicionar Grupo</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {taskGroups.map((group) => (
                <Card key={group.id} className="border border-border mb-4">
                  <CardHeader className="p-3 pb-2 flex flex-row items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Select 
                        value={group.icon} 
                        onValueChange={(value) => handleUpdateGroup(group.id, 'icon', value)}
                      >
                        <SelectTrigger className="w-14 h-8">
                          <SelectValue placeholder="Ícone" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="🎯">🎯</SelectItem>
                          <SelectItem value="🔧">🔧</SelectItem>
                          <SelectItem value="📚">📚</SelectItem>
                          <SelectItem value="🚀">🚀</SelectItem>
                          <SelectItem value="📋">📋</SelectItem>
                          <SelectItem value="🤝">🤝</SelectItem>
                          <SelectItem value="✅">✅</SelectItem>
                          <SelectItem value="⏱️">⏱️</SelectItem>
                          <SelectItem value="💬">💬</SelectItem>
                          <SelectItem value="📝">📝</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input 
                        value={group.title} 
                        onChange={(e) => handleUpdateGroup(group.id, 'title', e.target.value)} 
                        className="font-extrabold h-8 w-[300px]"
                        placeholder="Nome do grupo"
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleRemoveGroup(group.id)}
                      className="h-8 w-8"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId={group.id}>
                        {(provided) => (
                          <div 
                            className="space-y-2"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            <Table className="border">
                              <TableHeader>
                                <TableRow className="h-9 hover:bg-transparent">
                                  <TableHead className="w-10"></TableHead>
                                  <TableHead className="w-4/12">Tarefa</TableHead>
                                  <TableHead className="w-2/12">Tipo</TableHead>
                                  <TableHead className="w-2/12">Responsável</TableHead>
                                  <TableHead className="w-3/12">Prazo</TableHead>
                                  <TableHead className="w-1/12 text-right"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.tasks.map((task, index) => (
                                  <Draggable 
                                    key={task.id} 
                                    draggableId={task.id} 
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <TableRow 
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className="h-12 hover:bg-muted/30 group"
                                      >
                                        <TableCell className="p-1 pl-2">
                                          <div {...provided.dragHandleProps}>
                                            <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                                          </div>
                                        </TableCell>
                                        <TableCell className="p-1 pl-0 pr-2">
                                          <Input 
                                            value={task.title} 
                                            onChange={(e) => handleUpdateTask(group.id, task.id, 'title', e.target.value)} 
                                            className="h-8"
                                            placeholder="Título da tarefa"
                                          />
                                          <div className="mt-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Accordion type="single" collapsible className="w-full">
                                              <AccordionItem value="item-1" className="border-0">
                                                <AccordionTrigger className="py-1 text-xs text-muted-foreground hover:no-underline">
                                                  <span className="text-xs">Detalhes</span>
                                                </AccordionTrigger>
                                                <AccordionContent className="pb-0">
                                                  <div className="space-y-2">
                                                    <div>
                                                      <Label className="text-xs mb-1">Descrição</Label>
                                                      <Textarea
                                                        value={task.description || ""}
                                                        onChange={(e) => handleUpdateTask(group.id, task.id, "description", e.target.value)}
                                                        placeholder="Descrição breve da tarefa"
                                                        className="min-h-[60px] text-sm"
                                                        rows={2}
                                                      />
                                                    </div>
                                                    <div>
                                                      <Label className="text-xs mb-1">Descrição Detalhada</Label>
                                                      <Textarea
                                                        value={task.detailed_description || ""}
                                                        onChange={(e) => handleUpdateTask(group.id, task.id, "detailed_description", e.target.value)}
                                                        placeholder="Instruções detalhadas para completar esta tarefa"
                                                        className="min-h-[80px] text-sm"
                                                        rows={3}
                                                      />
                                                    </div>
                                                    <div>
                                                      <Label className="text-xs mb-1">Campos Personalizados</Label>
                                                      <CustomFieldsEditor
                                                        customFields={task.custom_fields}
                                                        onCustomFieldsChange={(customFields) => 
                                                          handleUpdateCustomFields(group.id, task.id, customFields)
                                                        }
                                                      />
                                                    </div>
                                                  </div>
                                                </AccordionContent>
                                              </AccordionItem>
                                            </Accordion>
                                          </div>
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Select 
                                            value={task.type} 
                                            onValueChange={(value) => handleUpdateTask(group.id, task.id, 'type', value)}
                                          >
                                            <SelectTrigger className="h-8">
                                              <SelectValue placeholder="Tipo" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="task">Tarefa Manual</SelectItem>
                                              <SelectItem value="approval">Aprovação</SelectItem>
                                              <SelectItem value="email_draft">E-mail Draft</SelectItem>
                                              <SelectItem value="email_auto_send">E-mail Auto-send</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Input 
                                            placeholder="Digite um responsável"
                                            className="h-8"
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' && e.currentTarget.value) {
                                                handleUpdateTask(
                                                  group.id, 
                                                  task.id, 
                                                  'assignees', 
                                                  [...(task.assignees || []), e.currentTarget.value]
                                                );
                                                e.currentTarget.value = '';
                                              }
                                            }}
                                          />
                                          {task.assignees && task.assignees.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {task.assignees.map((assignee, index) => (
                                                <div key={index} className="flex items-center gap-1 bg-blue-50 text-blue-700 px-1 py-0.5 rounded-sm text-xs">
                                                  <span className="max-w-[100px] truncate">{assignee}</span>
                                                  <button 
                                                    onClick={() => handleUpdateTask(
                                                      group.id, 
                                                      task.id, 
                                                      'assignees', 
                                                      task.assignees.filter((_, i) => i !== index)
                                                    )}
                                                    className="text-blue-700 hover:text-blue-900 h-4 w-4 flex items-center justify-center"
                                                  >
                                                    ×
                                                  </button>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <div className="flex items-center gap-1">
                                            <Input 
                                              type="number"
                                              min="1"
                                              className="w-12 h-8"
                                              value={task.deadline_value}
                                              onChange={(e) => handleUpdateTask(
                                                group.id, 
                                                task.id, 
                                                'deadline_value', 
                                                parseInt(e.target.value) || 1
                                              )}
                                            />
                                            <span className="text-xs text-muted-foreground mr-1">dias após</span>
                                            <Select 
                                              value={task.deadline_reference}
                                              onValueChange={(value) => handleUpdateTask(group.id, task.id, 'deadline_reference', value)}
                                            >
                                              <SelectTrigger className="h-8">
                                                <SelectValue placeholder="Referência" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="launch">Início</SelectItem>
                                                <SelectItem value="previous_task">Anterior</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </TableCell>
                                        <TableCell className="p-1 text-right">
                                          <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={() => handleRemoveTask(group.id, task.id)}
                                            className="h-8 w-8"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </TableBody>
                            </Table>
                            
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleAddTask(group.id)}
                              className="w-full h-8"
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Adicionar Tarefa
                            </Button>
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </CardContent>
                </Card>
              ))}
              
              {taskGroups.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Nenhum grupo de tarefas adicionado ainda. Clique em "Adicionar Grupo" para criar seu primeiro grupo.</p>
                </div>
              )}
              
              <Button variant="outline" onClick={() => setShowAddGroupDialog(true)} className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Grupo de Tarefas
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}